import {FC, useCallback, useMemo, useState} from 'react';
import {styled} from 'styled-components';

import {FullGroup, groupName, GroupType} from '@shared/models/api_models';

import {Button} from '@shared_web/components/core/button';
import {showAlert, showSuccess} from '@shared_web/components/core/notifications';
import {notifyError} from '@shared_web/lib/notification';

import {Translate, translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {BUTTON_THEME, SELECT_THEME, SELECT_WRAPPER_THEME} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {addDoorToGroup, useGroups} from '@mavismart_frontend/stores/groups_store';

interface AddDoorFromCustomGroupProps {
  doorId: string;
}

export const AddDoorFromCustomGroup: FC<AddDoorFromCustomGroupProps> = ({doorId}) => {
  const [selectedGroupIdAdd, setSelectedGroupIdAdd] = useState<string | undefined>();
  const account = useAccount();

  const fullGroups = useGroups();

  /// Get only custom groups and groups with admin permission
  const customGroups: FullGroup[] | undefined = useMemo(() => {
    if (!fullGroups) {
      return [];
    }
    return fullGroups.filter(g => g.group.type === GroupType.Manual && g.permission === 'admin');
  }, [fullGroups]);

  const handleSelectedGroupIdAddChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGroupIdAdd(e.target.value);
  }, []);

  const handleAddDoorToCustomGroup = useCallback(async () => {
    if (!selectedGroupIdAdd) {
      showAlert(translate('please_select_a_valid_group', getUserLanguage(account)));
      return;
    }
    try {
      await apiCall('POST /add-door-to-custom-group', {
        doorId,
        groupId: selectedGroupIdAdd,
      });
      showSuccess(translate('door_added_to_custom_group', getUserLanguage(account)));
      addDoorToGroup(doorId, selectedGroupIdAdd);
    } catch (error) {
      notifyError(error);
    }
  }, [selectedGroupIdAdd, account, doorId]);

  return customGroups.length === 0 ? (
    <></>
  ) : (
    <Wrapper>
      <Line style={{fontWeight: 600}}>
        <div style={{minWidth: 250}}>
          <Translate value="add_door_to_custom_group" />
        </div>

        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <SelectWrapper>
            <Styledselect value={selectedGroupIdAdd} onChange={handleSelectedGroupIdAddChange}>
              <option value="">
                <Translate value="select_group" />
              </option>
              {customGroups && customGroups.length > 0 ? (
                customGroups.map(g => (
                  <option key={g.group.id} value={g.group.id}>
                    {groupName(g)}
                  </option>
                ))
              ) : (
                <option value="">
                  {translate('no_custom_group_available', getUserLanguage(account))}
                </option>
              )}
            </Styledselect>
          </SelectWrapper>

          <Button themeOverrides={BUTTON_THEME} onClickAsync={handleAddDoorToCustomGroup}>
            <Translate value="add" />
          </Button>
        </div>
      </Line>
    </Wrapper>
  );
};

AddDoorFromCustomGroup.displayName = 'AddOrRemoveDoorFromCustomGroup';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Styledselect = styled.select`
  height: 40px;
  width: 200px;
  padding: 8px 40px 10px 12px;
  ${SELECT_THEME}
`;

const SelectWrapper = styled.div`
   ${SELECT_WRAPPER_THEME}
`;

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;
